import { Box, ButtonBase, ListItem, Typography } from "@mui/material";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { CheckIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import type { AnalysisVersionInfo } from "Infrastructure/ApiZfp/Schema";
import { useDateFnsLocale } from "Infrastructure/Hooks/DateFns/useDateFnsLocale";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { format } from "date-fns";
import type { Ref } from "react";
import { AnalysisStatusChip } from "../_Shared/AnalysisStatusChip";

const ROW_MIN_HEIGHT = 52;
const CHECK_SLOT_WIDTH = 16;

type Props = {
  readonly version: AnalysisVersionInfo;
  readonly isDisplayed: boolean;
  readonly buttonRef?: Ref<HTMLButtonElement>;
  readonly onOpen: (versionId: string) => void;
};

export const VersionSelectItem = ({ version, isDisplayed, buttonRef, onOpen }: Props) => {
  const { t } = useResource();
  const locale = useDateFnsLocale();

  return (
    <ListItem
      disablePadding
      sx={{
        borderRadius: Radius.M,
        // the lightest brand tint, so the displayed version is marked without the row turning salmon
        bgcolor: isDisplayed ? colors.brandLighter : "transparent",
        "&:hover": { bgcolor: isDisplayed ? colors.brandLighter : colors.backgroundLight },
      }}
    >
      <ButtonBase
        ref={buttonRef}
        aria-current={isDisplayed || undefined}
        onClick={() => onOpen(version.versionId)}
        sx={{
          width: "100%",
          minHeight: ROW_MIN_HEIGHT,
          py: 1.5,
          pl: Spacing.XL,
          pr: Spacing.M,
          gap: Spacing.L,
          justifyContent: "flex-start",
          textAlign: "left",
          borderRadius: Radius.M,
          "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
        }}
      >
        <Box component="span" display="flex" width={CHECK_SLOT_WIDTH} flexShrink={0} color="primary.main">
          {isDisplayed && <CheckIcon sx={{ fontSize: CHECK_SLOT_WIDTH }} />}
        </Box>
        <Box component="span" flex={1} minWidth={0}>
          <Typography variant="body2" component="span" display="block" noWrap fontWeight={500} color="text.primary">
            {t(Resources.AnalysisDetail.VersionSelect.Version, { version: version.version })}
          </Typography>
          <Typography variant="caption" component="span" display="block" noWrap color="text.secondary">
            {format(new Date(version.createdAt), "d. M. yyyy", { locale })}
          </Typography>
        </Box>
        <AnalysisStatusChip variant="filled" size="small" state={version.state} />
      </ButtonBase>
    </ListItem>
  );
};
